import { FilterHandler, GridOptions } from "ag-grid-community";
import { RefObject, useCallback, useMemo, useRef } from "react";

const supportedFilterTypes = new Set([
  "agNumberColumnFilter",
  "agTextColumnFilter",
]);

type GridContainerRef = Readonly<RefObject<HTMLElement>>;
type FloatingFilterOverrides = Pick<GridOptions, "onFloatingFilterUiChanged" | "enableFilterHandlers">;
export const useFloatingFilterOverrides = (gridContainerRef: GridContainerRef, debounceMs: number = 500): FloatingFilterOverrides => {
  const timeoutRef = useRef<Record<string, number>>({});
  const replaceTimeout = useCallback(
    (colId: string, handler: TimerHandler) => {
      const prevTimeout = timeoutRef.current[colId];
      if (prevTimeout != null) {
        clearTimeout(prevTimeout);
      }

      timeoutRef.current[colId] = setTimeout(handler, debounceMs);
    },
    [debounceMs],
  );

  const onFloatingFilterUiChanged = useCallback<NonNullable<GridOptions["onFloatingFilterUiChanged"]>>(
    (event) => {
      const filterType = event.column.getColDef().filter;
      if (!supportedFilterTypes.has(filterType)) {
        return;
      }

      const colId = event.column.getColId();
      replaceTimeout(colId, () => {
        // find the input element of the column's floating filter inside our grid container
        const floatingFilterInputElement = gridContainerRef.current?.querySelector<HTMLInputElement>(
          `.ag-floating-filter[col-id="${colId}"] input`,
        );

        if (floatingFilterInputElement != null) {
          let filterModel: unknown = undefined;
          const filterValue = floatingFilterInputElement.value;
          switch (filterType) {
            case "agNumberColumnFilter": {
              if (filterValue != null && filterValue.length > 0 && !Number.isNaN(+filterValue)) {
                filterModel = { filterType: "number", type: "equals", filter: +filterValue };
              }
              break;
            }

            case "agTextColumnFilter": {
              if (filterValue != null && filterValue.length > 0) {
                filterModel = { filterType: "text", type: "match", filter: filterValue };
              }
              break;
            }
            default: {
              return;
            }
          }

          const columnFilterHandler = event.api.getColumnFilterHandler<FilterHandler>(colId);
          if (filterModel != null && columnFilterHandler?.processModelToApply != null) {
            filterModel = columnFilterHandler.processModelToApply(filterModel);
          }

          const appliedFilterModel = event.api.getColumnFilterModel(event.column.getColId());
          if (JSON.stringify(appliedFilterModel) !== JSON.stringify(filterModel)) {
            event.api.setColumnFilterModel(event.column.getColId(), filterModel)
              .finally(() => event.api.onFilterChanged());
          }
        }
      });
    },
    [gridContainerRef, replaceTimeout],
  );

  return useMemo(
    () => ({
      onFloatingFilterUiChanged: onFloatingFilterUiChanged,
      enableFilterHandlers: true,
    }),
    [
      onFloatingFilterUiChanged,
    ],
  );
};
